import styles from '../TopBar.module.scss';
import {
  Button,
  Popover,
  Icon,
  MenuItem,
  MenuProvider,
  Avatar,
  Divider,
  IconButton,
  Typography,
  BUTTON_VARIANT,
  BUTTON_SIZE
} from '@sanlam-glacier/app-common/components';
import { ResultOfActingAsResponseViewModel, useGetActingAs } from 'bff';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'routes/route-paths';
import { UserSelectMenu } from './user-select-menu/UserSelectMenu';
import { UserSelectMenuSearch } from './user-select-menu/UserSelectMenuSearch';
import { useWindowDimensions } from 'hooks/useGetWindowDimensions';
import {
  allowActingAsAllAtom,
  getCurrentAuthMethod,
  isLoadingGetAccessAtom,
  isProxyAtom,
  proxyIntermediaryListAtom,
  useAuth
} from 'auth';
import { useConfig } from 'providers/config/ConfigProvider';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import { UserWithFeatures } from 'types/auth/user-context.type';
import { Backdrop } from 'components/molecules/backdrop/Backdrop';
import { LocationState } from 'types/routing/location-state';
import { useAtom } from 'jotai';
import { Theme } from 'components/atoms/theme/Theme';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';
import clsx from 'clsx';

const TOP_BAR_HEIGHT = 64;

export const ContextMenu = () => {
  const authApi = useAuth();
  const env = useConfig();
  const navigate = useNavigate();
  const userContextManagerApi = useUserContext();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [allowActingAsAll] = useAtom(allowActingAsAllAtom);
  const [isProxy] = useAtom(isProxyAtom);
  const [potentialIntermediaryToActAs, setPotentialIntermediaryToActAs] =
    useState<UserWithFeatures>();
  const [selectedActingAsIntermediary, setSelectedActingAsIntermediaryLocalState] = useState<
    UserWithFeatures | undefined
  >(userContextManagerApi.getActingAsIntermediaryUser());
  const [selectedActingAsSelf, setSelectedActingAsSelf] = useState<boolean | undefined>(
    userContextManagerApi.getActingAsIntermediaryUser()?.intermediaryCode ===
      userContextManagerApi.getCurrentAuthenticatedUserWithFeatures()?.intermediaryCode
  );

  if (
    selectedActingAsIntermediary === undefined &&
    userContextManagerApi.getActingAsIntermediaryUser()
  ) {
    setSelectedActingAsIntermediaryLocalState(userContextManagerApi.getActingAsIntermediaryUser());
    setSelectedActingAsSelf(
      userContextManagerApi.getActingAsIntermediaryUser()?.intermediaryCode ===
        userContextManagerApi.getCurrentAuthenticatedUserWithFeatures()?.intermediaryCode
    );
  }
  const identificationState = authApi.identificationState;
  const currentSignedInUser = authApi.getSignedInUser();
  const anchorElement = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const { height } = useWindowDimensions();
  const isUnidentified = identificationState === 'unidentified';
  const { isUsingGateway } = getCurrentAuthMethod(env);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const logIn = async () => {
    setSelectedActingAsIntermediary(undefined);
    await authApi.login({ authSuccessRedirect: location.pathname + location.search });
  };

  const logOut = async () => {
    closeMenu();
    setSelectedActingAsIntermediary(undefined);
    await authApi.logout();

    // Remove when Piral is removed
    sessionStorage.clear();

    userContextManagerApi.setActingAsIntermediaryUser(undefined);
    userContextManagerApi.setCurrentAuthenticatedUserWithFeatures(undefined);
  };

  const setSelectedActingAsIntermediary = (intermediary: UserWithFeatures | undefined) => {
    userContextManagerApi.setActingAsIntermediaryUser(intermediary);
    setSelectedActingAsIntermediaryLocalState(intermediary);
    setSelectedActingAsSelf(
      intermediary?.intermediaryCode ===
        userContextManagerApi.getCurrentAuthenticatedUserWithFeatures()?.intermediaryCode
    );
  };

  const discardActingAsIntermediary = () => {
    setSelectedActingAsIntermediary(undefined);
    navigate(routes.selectIntermediary);
    closeMenu();
  };

  const getLoggedInUserName = (): string => currentSignedInUser?.displayName ?? '';
  const getLoggedInEmail = (): string => currentSignedInUser?.email ?? '';
  const initials = (): string =>
    `${currentSignedInUser?.fullName?.[0] ?? ''} ${currentSignedInUser?.familyName?.[0] ?? ''}`;
  const getActingAsDisplayName = (intermediary: UserWithFeatures): string =>
    `${intermediary?.firstName ?? ''} ${intermediary?.lastName ?? ''}`;

  const handleSetSelectedProxy = async (newIntermediary: UserWithFeatures | undefined) => {
    if (newIntermediary) {
      setPotentialIntermediaryToActAs(newIntermediary);
      await mutateAsync({
        intermediaryCode: newIntermediary?.intermediaryCode
      });
    }
    setSelectedActingAsIntermediary(newIntermediary);
    navigate('/', { state: { skipBlock: true } satisfies LocationState });
  };

  const [isLoadingGetAccess] = useAtom(isLoadingGetAccessAtom);
  const [proxyIntermediaryList] = useAtom(proxyIntermediaryListAtom);

  const { mutateAsync, isPending } = useGetActingAs({
    onSuccess: (response: ResultOfActingAsResponseViewModel) => {
      const actingAsResult = response.data;
      if (actingAsResult?.allowedToActAs) {
        const user: UserWithFeatures = {
          features: potentialIntermediaryToActAs?.features ?? [],
          intermediaryCode: potentialIntermediaryToActAs?.intermediaryCode,
          firstName: actingAsResult.firstName,
          lastName: actingAsResult.surname
        };
        potentialIntermediaryToActAs &&
          displayToast({
            toastId: 'user-profile-menu-pilet:successful-proxy-login',
            title: 'Login successful',
            message: `${getLoggedInUserName()}  logged in as ${getActingAsDisplayName(user)}`,
            status: 'success'
          });
        setMenuOpen(false);
        setSelectedActingAsIntermediary(user);
        navigate('');
      }
    },
    meta: {
      errorMessage: 'Login unsuccessful'
    }
  });

  if (isUnidentified && isUsingGateway) {
    return (
      <Theme variant='secondary'>
        <Button variant={BUTTON_VARIANT.PRIMARY} size={BUTTON_SIZE.MEDIUM} onClick={logIn}>
          Log in
        </Button>
      </Theme>
    );
  }
  if (isUnidentified && !isUsingGateway) {
    // Returning a self closing div to ensure that children exist
    return <div />;
  }

  const showActingAsComponents = selectedActingAsIntermediary && !selectedActingAsSelf;

  return (
    <>
      <div>
        <div
          className={styles.userInfoSectionLoggedIn}
          ref={anchorElement}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Typography
            variant={Typography.VARIANT.B3}
            weight={Typography.WEIGHT.NORMAL}
            className={styles.userInfoUserName}
          >
            {getLoggedInUserName()}
          </Typography>
          {showActingAsComponents && selectedActingAsIntermediary.firstName && (
            <Typography variant={Typography.VARIANT.B3} weight={Typography.WEIGHT.NORMAL}>
              <span className={styles.actingAsDisplayName}>
                as {getActingAsDisplayName(selectedActingAsIntermediary)}
              </span>
            </Typography>
          )}
          <div className={styles.userInfoUserIcon}>
            <Theme variant='secondary'>
              <Avatar alt='profile image' initials={initials()} />
            </Theme>
          </div>
          <div className={styles.dropdownIcon}>
            {menuOpen ? (
              <Icon icon='navigation_expand-less' />
            ) : (
              <Icon icon='navigation_expand-more' />
            )}
          </div>
        </div>
      </div>
      <div className={styles.noPointer}>
        <Backdrop isOpen={menuOpen} onClick={() => setMenuOpen(false)} disableBackdropColor />
        <Popover
          aria-label='User context menu'
          open={menuOpen}
          position={Popover.POSITION.BOTTOM_END}
          className={styles.userInfoContextMenu}
          disablePadding
          anchorEl={anchorElement.current as HTMLElement}
          maxHeight={height - TOP_BAR_HEIGHT}
          mobileModal={false}
        >
          <div
            className={clsx(styles.exitBlock, styles.contextMenuBaseSidePadding)}
            onClick={() => closeMenu()}
          >
            <Icon icon='content_clear' className={styles.xxLargeIcon} />
          </div>
          <div className={clsx(styles.userBlock, styles.contextMenuLargeSidePadding)}>
            <div className={styles.userBlockContent}>
              <div>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B2}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  <strong>{getLoggedInUserName()}</strong>
                </Typography>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B4}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  {getLoggedInEmail()}
                </Typography>
              </div>
            </div>
          </div>
          <div className={clsx(styles.displayUserIcon, styles.contextMenuLargeSidePadding)}>
            <Theme variant='secondary'>
              <IconButton
                aria-label='User Info'
                variant={IconButton.VARIANT.PRIMARY}
                size={IconButton.SIZE.X_LARGE}
              >
                <Icon icon='social-media_person' className={styles.xxLargeIcon} />
              </IconButton>
            </Theme>
          </div>
          {showActingAsComponents && (
            <div className={styles.loggedInUserBlock}>
              <div className={styles.contextMenuExtraSidePadding}>
                <Typography
                  variant={Typography.VARIANT.O1}
                  color={Typography.COLOR.INHERIT}
                  className={styles.contextMenuBaseSidePadding}
                >
                  <span>LOGGED IN AS:</span>
                </Typography>
                <div className={clsx(styles.selectedProfile, styles.contextMenuBaseSidePadding)}>
                  <div>
                    <Typography
                      variant={Typography.VARIANT.B2}
                      weight={Typography.WEIGHT.NORMAL}
                      color={Typography.COLOR.INHERIT}
                    >
                      {selectedActingAsIntermediary.intermediaryCode}
                    </Typography>
                    <Typography
                      variant={Typography.VARIANT.B3}
                      weight={Typography.WEIGHT.NORMAL}
                      color={Typography.COLOR.INHERIT}
                    >
                      {selectedActingAsIntermediary.firstName}{' '}
                      {selectedActingAsIntermediary.lastName}
                    </Typography>
                  </div>
                  <IconButton
                    aria-label='Remove intermediary'
                    variant={IconButton.VARIANT.TERTIARY}
                    onClick={discardActingAsIntermediary}
                  >
                    <Icon
                      icon='navigation_cancel'
                      className={clsx(styles.xxLargeIcon, styles.pointer)}
                    />
                  </IconButton>
                </div>
              </div>
              <Divider />
            </div>
          )}
          {isProxy && !isPending ? (
            allowActingAsAll ? (
              <UserSelectMenuSearch
                isLoading={isLoadingGetAccess}
                selectedUserIntermediaryCode={selectedActingAsIntermediary?.intermediaryCode}
                setSelectedUser={async (user) => await handleSetSelectedProxy(user)}
              />
            ) : (
              <UserSelectMenu
                users={proxyIntermediaryList?.intermediaries ?? []}
                isLoading={isLoadingGetAccess}
                selectedUserIntermediaryCode={selectedActingAsIntermediary?.intermediaryCode}
                setSelectedUser={async (user) => await handleSetSelectedProxy(user)}
              />
            )
          ) : (
            <></>
          )}
          <Divider size={Divider.SIZE.SMALL} />
          <MenuProvider>
            <div className={styles.contextMenuBaseSidePadding}>
              <MenuItem onClick={logOut}>
                <div className={styles.menuItemWithIcon}>
                  <Icon
                    icon='action_sign-out'
                    className={clsx(styles.menuIcon, styles.xLargeIcon)}
                  />
                  <Typography
                    align={Typography.ALIGN.LEFT}
                    variant={Typography.VARIANT.B2}
                    weight={Typography.WEIGHT.NORMAL}
                  >
                    Log out
                  </Typography>
                </div>
              </MenuItem>
              <div className={styles.rowVersionId}>
                <Typography
                  align={Typography.ALIGN.LEFT}
                  variant={Typography.VARIANT.B4}
                  weight={Typography.WEIGHT.NORMAL}
                  color={Typography.COLOR.INHERIT}
                >
                  Version {env.versionId}
                </Typography>
              </div>
            </div>
          </MenuProvider>
        </Popover>
      </div>
    </>
  );
};
